// El mismo servicio web, con distinto nombre según el ambiente.
//
// Los servicios web de Business Central **se dan de alta a mano**, y el nombre
// con el que se registran no siempre coincide entre ambientes. El caso medido:
// el histórico de aperturas de caja se lee como `AperturaDeCaja` en desarrollo
// y **producción lo publica como `Apertura`**. El terminal lo tenía escrito
// fijo, así que en producción esa lectura devolvería un 404 y el histórico de
// aperturas no llegaría nunca.
//
// Es el mismo problema que ya resolvió `camposDeBusinessCentral.js` para los
// nombres de campo, y la misma solución: una cadena de candidatos en vez de un
// `if (ambiente)`. Con candidatos el terminal funciona en los dos sitios y no
// hay que acordarse de tocarlo el día que se despliegue; con un `if` habría que
// saber de antemano qué ambiente tiene cuál, que es justo lo que no se sabe.
//
// **Sólo se pasa al siguiente candidato cuando Business Central contesta que no
// existe.** Si la lectura falla por red, se propaga el fallo tal cual: probar
// otro nombre no arregla una conexión caída, y peor, dejaría cacheado el nombre
// equivocado en cuanto la red volviera.
import { crearRegistro } from "../utils/registro";

const registro = crearRegistro("entidades de BC");

/**
 * Los nombres con los que puede estar registrado cada servicio, en orden de
 * preferencia. El primero es el que usan los ambientes de desarrollo, que es
 * donde se prueba.
 */
export const CANDIDATOS = {
  aperturas: ["AperturaDeCaja", "Apertura"],
};

/** El nombre que funcionó, por ambiente y entidad. */
const resueltas = new Map();

const laClave = (ambiente, entidad) => `${ambiente || "(sin ambiente)"}|${entidad}`;

/** Si Business Central contestó que ese nombre no está publicado. */
const noEstaPublicada = (error) => error?.response?.status === 404;

/**
 * Lee una entidad probando sus nombres hasta que uno conteste.
 *
 * El nombre que funciona se recuerda para ese ambiente, así que el coste de
 * probar se paga una vez por sesión y no en cada lectura.
 *
 * @param {object} datos
 * @param {string} datos.entidad   La clave de `CANDIDATOS`, por ejemplo `"aperturas"`.
 * @param {string} datos.ambiente  El ambiente activo, para no mezclar cachés.
 * @param {(nombre: string) => Promise<*>} datos.leer  Cómo se lee, dado el nombre.
 * @returns {Promise<*>} Lo que devuelva `leer`.
 */
export const leerEntidad = async ({ entidad, ambiente, leer }) => {
  const candidatos = CANDIDATOS[entidad];
  if (!candidatos) throw new Error(`No hay candidatos para la entidad «${entidad}»`);

  const clave = laClave(ambiente, entidad);
  const recordado = resueltas.get(clave);
  if (recordado) return leer(recordado);

  let ultimoError = null;

  for (const nombre of candidatos) {
    try {
      const resultado = await leer(nombre);
      resueltas.set(clave, nombre);
      if (nombre !== candidatos[0]) {
        registro.log(`«${entidad}» se publica en ${ambiente} como «${nombre}»`);
      }
      return resultado;
    } catch (error) {
      if (!noEstaPublicada(error)) throw error;
      registro.warn(`«${nombre}» no está publicada en ${ambiente}; se prueba el siguiente nombre`);
      ultimoError = error;
    }
  }

  // Ninguno existe. Se devuelve el último fallo, que ya trae el mensaje bueno de
  // `nombrarErrorDeLectura` y su marca de que no se reintenta.
  throw ultimoError;
};

/** El nombre que se resolvió, o `null`. Para poder enseñarlo en el censo. */
export const nombreResuelto = (entidad, ambiente) =>
  resueltas.get(laClave(ambiente, entidad)) || null;

/** Sólo para las pruebas y para cuando se cambia de ambiente. */
export const olvidarEntidadesResueltas = () => resueltas.clear();
